/**
 * ReactivationRequestList Component
 * Displays and manages pending reactivation requests from disabled accounts
 * 
 * Requirements: 17.4, 17.5, 17.6
 */

import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

// GraphQL Queries and Mutations
export const LIST_REACTIVATION_REQUESTS = gql`
  query ListReactivationRequests($status: ReactivationRequestStatus, $limit: Int, $nextToken: String) {
    listReactivationRequests(status: $status, limit: $limit, nextToken: $nextToken) {
      items {
        id
        userId
        requestedAt
        status
        resolvedBy
        resolvedAt
        notes
        user {
          id
          firstName
          lastName
          email
        }
      }
      nextToken
    }
  }
`;

export const RESOLVE_REACTIVATION_REQUEST = gql`
  mutation ResolveReactivationRequest($id: ID!, $approved: Boolean!, $notes: String) {
    resolveReactivationRequest(id: $id, approved: $approved, notes: $notes) {
      id
      status
      resolvedBy
      resolvedAt
      notes
    }
  }
`;

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface ReactivationRequest {
  id: string;
  userId: string;
  requestedAt: string;
  status: 'PENDING' | 'APPROVED' | 'DENIED';
  resolvedBy?: string;
  resolvedAt?: string;
  notes?: string;
  user?: User;
}

interface ListReactivationRequestsResponse {
  listReactivationRequests: {
    items: ReactivationRequest[];
    nextToken?: string;
  };
}

interface ReactivationRequestListProps {
  /** Filter by status - defaults to PENDING */
  statusFilter?: 'PENDING' | 'APPROVED' | 'DENIED' | 'ALL';
  /** Maximum number of items to display */
  limit?: number;
  /** Show as a compact widget or full page view */
  compact?: boolean;
}

export function ReactivationRequestList({
  statusFilter = 'PENDING',
  limit = 20,
  compact = false,
}: ReactivationRequestListProps) {
  const navigate = useNavigate();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<ListReactivationRequestsResponse>(
    LIST_REACTIVATION_REQUESTS,
    {
      variables: {
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        limit,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [resolveRequest] = useMutation(RESOLVE_REACTIVATION_REQUEST, {
    onCompleted: () => {
      refetch();
    },
  });

  const handleEnableAccount = async (requestId: string, userName: string) => {
    setProcessingId(requestId);
    setSuccessMessage(null);

    try {
      await resolveRequest({
        variables: {
          id: requestId,
          approved: true,
          notes: 'Account enabled via reactivation request list',
        },
      });
      setSuccessMessage(`Account for ${userName} has been enabled successfully.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error enabling account:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleViewClient = (userId: string) => {
    navigate(`/clients/${userId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeVariant = (status: string): 'default' | 'success' | 'warning' | 'info' => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'DENIED':
        return 'default';
      default:
        return 'info';
    }
  };

  const requests = data?.listReactivationRequests?.items || [];

  if (loading && !data) {
    return (
      <Card className={compact ? '' : 'max-w-4xl mx-auto'}>
        <CardHeader>
          <CardTitle>Reactivation Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={compact ? '' : 'max-w-4xl mx-auto'}>
        <CardHeader>
          <CardTitle>Reactivation Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600">Error loading reactivation requests</p>
            <p className="text-sm text-gray-500 mt-2">{error.message}</p>
            <Button onClick={() => refetch()} variant="outline" className="mt-4">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={compact ? '' : 'max-w-4xl mx-auto'}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            Reactivation Requests
            {statusFilter === 'PENDING' && requests.length > 0 && (
              <Badge variant="warning" className="ml-2">
                {requests.length}
              </Badge>
            )}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-green-800">{successMessage}</span>
          </div>
        )}

        {/* Request List */}
        <div className={`space-y-3 ${compact ? 'max-h-96 overflow-y-auto' : ''}`}>
          {requests.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-500">No {statusFilter === 'ALL' ? '' : statusFilter.toLowerCase()} reactivation requests</p>
            </div>
          ) : (
            requests.map((request) => (
              <div
                key={request.id}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Client Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">
                        {request.user?.firstName} {request.user?.lastName}
                      </span>
                      <Badge variant={getStatusBadgeVariant(request.status)}>
                        {request.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 truncate">
                      {request.user?.email}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Requested: {formatDate(request.requestedAt)}
                    </p>
                    {request.resolvedAt && (
                      <p className="text-xs text-gray-500">
                        Resolved: {formatDate(request.resolvedAt)}
                      </p>
                    )}
                    {request.notes && (
                      <p className="text-xs text-gray-500 mt-1 italic">
                        Note: {request.notes}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {request.status === 'PENDING' && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleEnableAccount(
                          request.id,
                          `${request.user?.firstName} ${request.user?.lastName}`
                        )}
                        disabled={processingId === request.id}
                      >
                        {processingId === request.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Enable Account
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewClient(request.userId)}
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Client
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Load More (if pagination is needed) */}
        {data?.listReactivationRequests?.nextToken && (
          <div className="mt-4 text-center">
            <Button variant="outline" onClick={() => {
              // Pagination can be implemented here if needed
              console.log('Load more with nextToken:', data.listReactivationRequests.nextToken);
            }}>
              Load More
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ReactivationRequestList;
